// Tiempo real (contratos, «Tiempo real»): Socket.IO en el mismo origen, con la cookie de sesión y el
// csrfToken en memoria. Los avisos no traen datos: solo dicen qué refrescar por HTTP. Un aviso perdido
// no es un mensaje perdido: el sondeo de respaldo sigue activo (queries.ts, pollInterval).
import type { HiloChanged } from '@hilo/contracts';
import { io, type Socket } from 'socket.io-client';
import { getCsrfToken } from './api';

export type LiveState = 'connecting' | 'live' | 'offline';

/** Rechazos del handshake que exigen comprobar la sesión antes de reintentar. */
const AUTH_ERRORS = new Set(['UNAUTHENTICATED', 'CSRF_INVALID', 'PASSWORD_CHANGE_REQUIRED', 'ORIGIN_MISMATCH']);
const MAX_AUTH_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 10_000;
const CHECK_TIMEOUT_MS = 10_000;

let state: LiveState = 'offline';
const listeners = new Set<() => void>();

function setState(next: LiveState): void {
  if (next === state) return;
  state = next;
  for (const listener of listeners) listener();
}

export function liveState(): LiveState {
  return state;
}

export function subscribeLive(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export interface LiveHandlers {
  /** Un aviso del servidor: refrescar lo que corresponda. */
  onChange: (change: HiloChanged) => void;
  /** Tras reconectar: pudo perderse cualquier aviso, se refresca todo. */
  onReconnect: () => void;
  /**
   * El servidor cerró o rechazó el canal (acceso o sesión). Devuelve true si la sesión sigue valiendo
   * y se puede reintentar; false solo ante 401/403 (la app ya habrá cerrado la sesión local).
   * Los fallos temporales se propagan para volver a comprobar, sin consumir intentos de auth.
   */
  onRejected: (signal: AbortSignal) => Promise<boolean>;
}

/** Abre el canal en vivo. Devuelve la función para cerrarlo (al salir de la bandeja o cerrar sesión). */
export function startLive(handlers: LiveHandlers): () => void {
  let everConnected = false;
  let authRetries = 0;
  let stopped = false;
  let retryDelay = RETRY_DELAY_MS;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let checking = false;
  let needsSessionCheck = false;
  let checkController: AbortController | undefined;
  setState('connecting');

  const socket: Socket = io({
    path: '/socket.io',
    withCredentials: true,
    // Conexión propia en cada arranque: el gestor compartido de socket.io queda cerrado tras un
    // disconnect() (p. ej. el doble montaje de React en desarrollo) y no se reutiliza.
    forceNew: true,
    // Solo websocket: en él el navegador siempre envía Origin, que el servidor exige. El «polling» inicial
    // de Socket.IO usa GET del mismo origen, sin Origin, y la API lo rechaza (403).
    transports: ['websocket'],
    // El token se lee en cada intento: tras recuperar la sesión puede haber cambiado.
    auth: (callback) => callback({ csrfToken: getCsrfToken() }),
    // Un único bucle controla tanto cortes de transporte como cierres explícitos del servidor.
    reconnection: false,
    timeout: CHECK_TIMEOUT_MS,
  });

  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(retryTimer);
    checkController?.abort();
    socket.removeAllListeners();
    socket.disconnect();
    setState('offline');
  };

  const scheduleRetry = (checkSession: boolean) => {
    if (stopped) return;
    needsSessionCheck ||= checkSession;
    setState('connecting');
    if (retryTimer !== undefined || checking) return;
    retryTimer = setTimeout(() => {
      retryTimer = undefined;
      void retry();
    }, retryDelay);
    retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY_MS);
  };

  const retry = async () => {
    if (stopped) return;
    if (needsSessionCheck) {
      checking = true;
      const controller = new AbortController();
      checkController = controller;
      const timeout = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
      let valid: boolean;
      try {
        valid = await handlers.onRejected(controller.signal);
      } catch {
        // Sin respuesta o con 5xx no sabemos si la sesión vale: conservarla y comprobar más tarde.
        checking = false;
        scheduleRetry(true);
        return;
      } finally {
        clearTimeout(timeout);
        checkController = undefined;
        checking = false;
      }
      if (stopped) return;
      if (!valid) {
        stop();
        return;
      }
      needsSessionCheck = false;
    }
    socket.connect();
  };

  socket.on('connect', () => {
    authRetries = 0;
    retryDelay = RETRY_DELAY_MS;
    clearTimeout(retryTimer);
    retryTimer = undefined;
    setState('live');
    if (everConnected) handlers.onReconnect();
    everConnected = true;
  });
  socket.on('hilo.changed', (change: HiloChanged) => handlers.onChange(change));
  socket.on('disconnect', (reason) => {
    // Un cierre ordenado también ocurre al reiniciar la API; no equivale a una sesión revocada.
    if (reason === 'io client disconnect') stop();
    else scheduleRetry(reason === 'io server disconnect');
  });
  socket.on('connect_error', (error: Error) => {
    const authError = AUTH_ERRORS.has(error.message);
    if (authError) {
      if (authRetries >= MAX_AUTH_RETRIES) {
        stop();
        return;
      }
      authRetries += 1;
    }
    scheduleRetry(authError);
  });

  return stop;
}
